import { Navigate } from 'react-router-dom'
import { useAthletes, useMe } from '@/api/hooks'
import { Skeleton } from '@/components/ui/Skeleton'
import { ErrorState } from '@/components/ui/ErrorState'
import { EmptyState } from '@/components/ui/EmptyState'
import { Users } from 'lucide-react'
import { isAuthError } from '@/api/queryClient'

/**
 * Resolves "/" to `/a/:slug/today` for the viewer's own athlete, or the first athlete. A signed-in
 * account with no athlete of its own goes to /onboarding to connect intervals.icu.
 */
export default function RootRedirect() {
  const me = useMe()
  const athletes = useAthletes()

  // A sign-in error is already redirecting (see createQueryClient): hold the placeholder.
  const redirecting = isAuthError(me.error) || isAuthError(athletes.error)
  if (me.isLoading || athletes.isLoading || redirecting) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg p-6">
        <Skeleton className="h-9 w-48" />
      </div>
    )
  }

  if (me.data?.mustChangePassword) return <Navigate to="/welcome" replace />
  // A signed-in account with no athlete of its own connects intervals.icu first.
  if (me.data?.auth === 'session' && !me.data.athleteId) {
    return <Navigate to="/onboarding" replace />
  }

  if (athletes.isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg p-6">
        <ErrorState
          message={
            athletes.error instanceof Error ? athletes.error.message : 'Could not load athletes.'
          }
          onRetry={() => void athletes.refetch()}
        />
      </div>
    )
  }

  const list = athletes.data ?? []
  if (list.length === 0) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg p-6">
        <EmptyState
          icon={Users}
          title="No athletes yet"
          body="Connect an athlete in intervals.icu and run a sync."
        />
      </div>
    )
  }

  const mine = me.data?.athleteId ? list.find((a) => a.id === me.data!.athleteId) : undefined
  const target = mine ?? list[0]!

  return <Navigate to={`/a/${target.slug}/today`} replace />
}
